import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Star, Lightbulb, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';

interface QuestionDisplayProps {
  validBig3Display: string[];
  validBackupDisplay: string[];
  showInCanvas?: boolean;
}

// Canvas version - Big 3 always visible, backup questions collapsible below
export const QuestionDisplayCanvas = ({ validBig3Display, validBackupDisplay }: QuestionDisplayProps) => {
  const [isBackupExpanded, setIsBackupExpanded] = useState(false);

  return (
    <div className="fixed top-20 left-4 w-80 bg-white rounded-xl shadow-lg border border-amber-200 z-30 max-h-[calc(100vh-5rem)] overflow-y-auto">
      {/* Big 3 Questions - Always Visible */}
      <div className="p-6 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-t-xl">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full">
            <Star className="w-4 h-4 text-white fill-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-amber-900">Your Big 3 Questions</h3>
            <p className="text-xs text-amber-700 opacity-80">Core questions that guide your entire interview</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {validBig3Display.length > 0 ? (
            validBig3Display.map((question, index) => (
              <div key={index} className="group relative">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-200 hover:shadow-md transition-all duration-200 hover:border-amber-300">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 leading-relaxed">
                        {question}
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-amber-200 border-dashed">
              <div className="text-center">
                <Star className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-sm text-amber-700 font-medium">No Big 3 questions set up yet</p>
                <p className="text-xs text-amber-600 opacity-80 mt-1">These will guide your entire interview</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backup Questions - Collapsible Section */}
      {validBackupDisplay.length > 0 && (
        <div className="border-t border-amber-200">
          <Collapsible open={isBackupExpanded} onOpenChange={setIsBackupExpanded}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full group hover:bg-blue-50 p-4 h-auto transition-all duration-200 rounded-none"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                      <Lightbulb className="w-3 h-3 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-semibold text-blue-900">Backup Questions</span>
                      <p className="text-xs text-blue-700 opacity-80">Ready when you need to dig deeper</p>
                    </div>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {validBackupDisplay.length}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-blue-600 font-medium">
                      {isBackupExpanded ? 'Hide' : 'Show'}
                    </span>
                    {isBackupExpanded ? 
                      <ChevronUp className="w-4 h-4 text-blue-600" /> : 
                      <ChevronDown className="w-4 h-4 text-blue-600" />
                    }
                  </div>
                </div>
              </Button>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="bg-blue-50/50">
              <div className="p-4">
                <div className="space-y-2">
                  {validBackupDisplay.map((question, index) => (
                    <div key={index} className="group relative">
                      <div className="bg-white rounded-lg p-3 border border-blue-100 hover:border-blue-200 transition-all duration-200 hover:shadow-sm">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs flex items-center justify-center font-medium">
                            {index + 1}
                          </div>
                          <p className="text-xs text-gray-800 leading-relaxed font-medium">
                            {question}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-blue-100">
                  <Sparkles className="w-3 h-3 text-blue-500" />
                  <p className="text-xs text-blue-600 font-medium">Use these when conversations stall or you need deeper insights</p>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
};

// Original sidebar version for GuidancePanel
export const QuestionDisplay = ({ validBig3Display, validBackupDisplay }: QuestionDisplayProps) => {
  const [isBackupExpanded, setIsBackupExpanded] = useState(false);

  return (
    <div className="p-6 border-b border-gray-200 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Big 3 Questions - Hero Section */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full">
            <Star className="w-4 h-4 text-white fill-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-amber-900">Your Big 3 Questions</h3>
            <p className="text-xs text-amber-700 opacity-80">Core questions that guide your entire interview</p>
          </div>
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 font-semibold">
            Primary Focus
          </Badge>
        </div>
        
        <div className="space-y-3">
          {validBig3Display.length > 0 ? (
            validBig3Display.map((question, index) => (
              <div key={index} className="group relative">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-200 hover:shadow-md transition-all duration-200 hover:border-amber-300">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 leading-relaxed">
                        {question}
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-amber-200 border-dashed">
              <div className="text-center">
                <Star className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-sm text-amber-700 font-medium">No Big 3 questions set up yet</p>
                <p className="text-xs text-amber-600 opacity-80 mt-1">These will guide your entire interview</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backup Questions - Collapsible Section */}
      {validBackupDisplay.length > 0 && (
        <Collapsible open={isBackupExpanded} onOpenChange={setIsBackupExpanded}>
          <CollapsibleTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full group hover:bg-white/70 border border-blue-200 rounded-lg p-4 h-auto transition-all duration-200"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                    <Lightbulb className="w-3 h-3 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-semibold text-blue-900">Backup Questions</span>
                    <p className="text-xs text-blue-700 opacity-80">Ready when you need to dig deeper</p>
                  </div>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {validBackupDisplay.length}
                  </Badge>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-blue-600 font-medium">
                    {isBackupExpanded ? 'Hide' : 'Show'}
                  </span>
                  {isBackupExpanded ? 
                    <ChevronUp className="w-4 h-4 text-blue-600" /> : 
                    <ChevronDown className="w-4 h-4 text-blue-600" />
                  }
                </div>
              </div>
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="mt-3">
            <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-blue-100">
              <div className="space-y-2">
                {validBackupDisplay.map((question, index) => (
                  <div key={index} className="group relative">
                    <div className="bg-white rounded-lg p-3 border border-blue-100 hover:border-blue-200 transition-all duration-200 hover:shadow-sm">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs flex items-center justify-center font-medium">
                          {index + 1}
                        </div>
                        <p className="text-xs text-gray-800 leading-relaxed font-medium">
                          {question}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-blue-100">
                <Sparkles className="w-3 h-3 text-blue-500" />
                <p className="text-xs text-blue-600 font-medium">Use these when conversations stall or you need deeper insights</p>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};